import SliderMedia from '@/app/base-ui/SwiperSliderMedia';
import FallbackUI from '@/app/components/MUI/FallBackImageUI';
import { Banner } from '@/app/models/banner/BannerModel';
import { Box, Skeleton, useMediaQuery, useTheme } from '@mui/material';
import CenteredBox from '@/app/base-ui/CenterBox';
import { Autoplay } from 'swiper/modules';
import utilsService from '@/app/helpers/utilsService';
import { useRouter } from 'next/navigation';
import ResponsiveImage from '@/app/components/MUI/ResponsiveImage';
import { useHomeData } from '@/app/contexts/home-data-context';
import { useEffect, useRef } from 'react';
import { homeService } from '@/app/service/homeService';

const MainBanner = () => {
    const { homeData, isLoading } = useHomeData();
    const router = useRouter();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down(600));
    // U6-14: 배너가 실제로 화면에 노출된 시점에만, 배너당 1회 view_count 를 올린다
    const trackedBannerIdsRef = useRef<Set<number>>(new Set());
    const trackBannerView = (banner?: Banner) => {
        if (!banner?.id || trackedBannerIdsRef.current.has(banner.id)) {
            return;
        }
        trackedBannerIdsRef.current.add(banner.id);
        homeService.trackBannerView(banner.id).catch(() => {});
    };

    useEffect(() => {
        // 스켈레톤이 끝나고 첫 배너가 화면에 그려지는 시점 = 노출 시점
        if (!isLoading && homeData?.banners && homeData.banners.length > 0) {
            trackBannerView(homeData.banners[0]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoading, homeData?.banners]);

    const handleLoaded = (e: any) => {
        // setIsLoading(false);
    };

    function gotoLink(url: string) {
        if (url && utilsService.isUrl(url)) {
            const parsedUrl = new URL(url);
            const domain = utilsService.normalizeDomain(parsedUrl.hostname);
            const currentDomain = utilsService.getAppDomain();
            // console.log('domain', domain, 'currentDomain', currentDomain);
            if (domain === currentDomain) {
                router.push(parsedUrl.pathname);
            } else {
                window.open(url, '_blank');
            }
        }
    }

    const bannerData = homeData?.banners;

    // If no banners, skip skeleton and return null
    if (!bannerData || bannerData.length < 1) {
        return null;
    }

    if (isLoading || !homeData) {
        return (
            <Box sx={{ position: 'relative' }}>
                <Skeleton
                    sx={{ height: 240 }}
                    animation="wave"
                    variant="rectangular"
                />
                <CenteredBox sx={{ flexDirection: 'row', gap: 2, position: 'absolute', bottom: 20 }}>
                    <Skeleton
                        animation="wave"
                        variant="circular"
                        width={12}
                        height={12}
                    />
                    <Skeleton
                        animation="wave"
                        variant="circular"
                        width={12}
                        height={12}
                    />
                    <Skeleton
                        animation="wave"
                        variant="circular"
                        width={12}
                        height={12}
                    />
                </CenteredBox>
            </Box>
        );
    }

    return (
        <SliderMedia
            bulletType="white"
            data={bannerData}
            showNumbersImg={bannerData.length > 1}
            slidesPerView={1}
            autoplay={{
                delay: 3000,
            }}
            loop={bannerData.length > 1}
            module={bannerData.length > 1 ? [Autoplay] : []}
            showButtonNextPrev={false}
            onSlideChange={(index) => {
                // U6-14: 슬라이드가 넘어가 다른 배너가 보이게 될 때도 노출로 기록한다
                trackBannerView(bannerData[index]);
            }}
        >
            {(data: Banner, _index, hasError, handleError) =>
                hasError ? (
                    <FallbackUI
                        width={'100%'}
                        maxWidth={'100%'}
                        height={isMobile ? '260px' : '471px'}
                        maxHeight={isMobile ? '260px' : '471px'}
                        marginRight="0"
                        description="The url or image is invalid"
                    />
                ) : (
                    <ResponsiveImage
                        image={data?.image?.original}
                        imageMobile={data?.imageMobile?.original}
                        alt="Home Banner"
                        link={data.link ?? ''}
                        style={{ maxHeight: isMobile ? 260 : 471 }}
                        isFixedHeight={true}
                        priority={_index === 0}
                        onError={handleError}
                        onLoad={handleLoaded}
                        onClick={() => gotoLink(data.link ?? '')}
                    />
                )
            }
        </SliderMedia>
    );
};

export default MainBanner;
